// Configuration des écrans de liste/formulaire (voir ResourcePage et
// ResourceForm). Une configuration décrit : l'API, les permissions, les
// colonnes, les filtres et les champs du formulaire.
//
// Rappels du contrat d'API : montants en chaînes (deux décimales au plus,
// strictement positifs), dates métier YYYY-MM-DD ne dépassant pas demain
// (`business: true`), champs `nullable` remis à null quand ils sont vidés.

import { api } from '@/api'
import { session } from '@/stores/session'
import { enumOptions } from '@/utils/labels'
import { formatNumber } from '@/utils/money'
import { formatDate } from '@/utils/dates'

const yesNo = [
  { value: '1', label: 'Oui' },
  { value: '0', label: 'Non' }
]

const vehicleText = (v) => (v ? v.registration_number : '—')

// ─────────── Compte de connexion à la création d'une fiche (propriétaire, chauffeur) ───────────
// Case « Donner un accès » + champs du compte : envoyés sous la clé `account` du même appel
// (tout ou rien). `account` et `user_id` ne s'envoient jamais ensemble, et `PUT` refuse `account`.
// Réservé aux utilisateurs ayant `users.create`.
const accountFields = (who) => [
  { name: 'give_access', label: 'Donner un accès à l’application', type: 'toggle', virtual: true, createOnly: true, default: false, wide: true, visible: () => session.can('users.create'), hint: `Crée en même temps le compte de connexion ${who}.` },
  { name: 'account_email', errorKey: 'account.email', label: 'E-mail de connexion', type: 'email', virtual: true, createOnly: true, max: 255, autocomplete: 'off', visible: (f) => f.give_access },
  { name: 'account_phone', errorKey: 'account.phone', label: 'Téléphone de connexion', type: 'tel', virtual: true, createOnly: true, max: 30, placeholder: '+242064265595', hint: 'Format international conseillé.', visible: (f) => f.give_access },
  // Le mot de passe est requis dès que `account` est envoyé (la spécification générée ne sait pas l'exprimer).
  { name: 'account_password', errorKey: 'account.password', label: 'Mot de passe', type: 'password', virtual: true, createOnly: true, required: true, autocomplete: 'new-password', generate: true, hint: 'Au moins 8 caractères.', notice: 'Si un e-mail est renseigné, la personne sera prévenue de la création de son compte. Le mot de passe ne lui est pas envoyé : communiquez-le-lui vous-même.', visible: (f) => f.give_access },
  { name: 'account_is_active', errorKey: 'account.is_active', label: 'Compte actif', type: 'toggle', virtual: true, createOnly: true, default: true, visible: (f) => f.give_access }
]

const accountHooks = {
  validate(f) {
    if (!f.give_access) return {}
    const errors = {}
    if (!String(f.account_email).trim() && !String(f.account_phone).trim()) errors.account_email = ['Indiquez un e-mail ou un téléphone de connexion.']
    if (f.account_password && f.account_password.length < 8) errors.account_password = ['Au moins 8 caractères.']
    return errors
  },
  toPayload(form, { defaults }) {
    if (!form.give_access) return defaults
    // Avec « account », on n'envoie pas « user_id ».
    const fiche = { ...defaults }
    delete fiche.user_id
    const account = { password: form.account_password, is_active: !!form.account_is_active }
    if (String(form.account_email).trim()) account.email = form.account_email.trim()
    if (String(form.account_phone).trim()) account.phone = form.account_phone.trim()
    return { ...fiche, account }
  },
  // Cas rare (deux créations simultanées) : l'erreur peut arriver sous « email » ou « phone »
  // au lieu de « account.email » ; on la rattache alors au compte plutôt qu'au contact.
  mapErrors(errors, form) {
    if (!form.give_access) return errors
    const out = { ...errors }
    for (const key of ['email', 'phone']) {
      if (out[key]) {
        out[`account.${key}`] = [...(out[`account.${key}`] || []), ...out[key]]
        delete out[key]
      }
    }
    return out
  }
}

// ───────────────────────────── Propriétaires ─────────────────────────────
export const ownersConfig = {
  key: 'owners',
  realtime: ['vehicles'], // colonne « Véhicules »
  title: 'Propriétaires',
  subtitle: 'Les propriétaires des véhicules de la flotte.',
  singular: 'propriétaire',
  icon: 'home',
  api: api.owners,
  permissions: { create: 'owners.create', update: 'owners.update', delete: 'owners.delete' },
  searchPlaceholder: 'Nom, téléphone, e-mail, pièce d’identité…',
  defaultSort: 'name',
  itemLabel: (r) => r.name,
  filters: [{ key: 'is_active', label: 'Statut', options: [{ value: '1', label: 'Actifs' }, { value: '0', label: 'Inactifs' }], blank: 'Tous' }],
  columns: [
    { key: 'name', label: 'Nom', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'email', label: 'E-mail' },
    { key: 'vehicles_count', label: 'Véhicules', align: 'right' },
    { key: 'is_active', label: 'Statut', badge: 'activeFlag' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 255 },
    // Coordonnées de la fiche : de simples contacts, pas des identifiants de connexion.
    { name: 'phone', label: 'Téléphone (contact)', type: 'tel', nullable: true, max: 30 },
    { name: 'email', label: 'E-mail (contact)', type: 'email', nullable: true, max: 255 },
    { name: 'id_number', label: 'Numéro de pièce d’identité', type: 'text', nullable: true, max: 50 },
    { name: 'address', label: 'Adresse', type: 'text', nullable: true, max: 255, wide: true },
    // Lien manuel vers un compte existant. « account » et « user_id » ne s'envoient jamais ensemble.
    { name: 'user_id', label: 'Compte de connexion (portail)', type: 'lookup', lookup: 'users', blank: 'Aucun compte lié', nullable: true, wide: true, hint: 'Pour remplacer un lien, retirez-le d’abord (« Aucun compte lié »), puis enregistrez.', visible: (f, editing) => editing || !f.give_access },
    ...accountFields('du propriétaire (rôle propriétaire)'),
    { name: 'notes', label: 'Notes', type: 'textarea', nullable: true, max: 2000, wide: true },
    { name: 'is_active', label: 'Propriétaire actif', type: 'toggle', default: true }
  ],
  ...accountHooks
}

// ─────────────────────────── Types de véhicules ───────────────────────────
export const vehicleTypesConfig = {
  key: 'vehicle-types',
  realtime: ['vehicles'],
  title: 'Types de véhicules',
  subtitle: 'Chaque type porte le quota journalier par défaut de ses véhicules.',
  singular: 'type de véhicule',
  icon: 'tag',
  api: api.vehicleTypes,
  permissions: { create: 'vehicle_types.create', update: 'vehicle_types.update', delete: 'vehicle_types.delete' },
  searchPlaceholder: 'Nom, description…',
  defaultSort: 'name',
  itemLabel: (r) => r.name,
  filters: [{ key: 'is_active', label: 'Statut', options: [{ value: '1', label: 'Actifs' }, { value: '0', label: 'Inactifs' }] }],
  columns: [
    { key: 'name', label: 'Nom', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'description', label: 'Description' },
    { key: 'default_daily_quota', label: 'Quota journalier', money: true, align: 'right' },
    { key: 'vehicles_count', label: 'Véhicules', align: 'right' },
    { key: 'is_active', label: 'Statut', badge: 'activeFlag' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 100 },
    { name: 'default_daily_quota', label: 'Quota journalier par défaut', type: 'money', allowZero: true },
    { name: 'description', label: 'Description', type: 'text', nullable: true, max: 255, wide: true },
    { name: 'is_active', label: 'Type actif', type: 'toggle', default: true }
  ]
}

// ─────────────────────── Catégories de dépenses ───────────────────────────
export const expenseCategoriesConfig = {
  key: 'expense-categories',
  realtime: ['expenses'],
  title: 'Catégories de dépenses',
  subtitle: 'Classez les dépenses de la flotte.',
  singular: 'catégorie',
  feminine: true,
  icon: 'tag',
  api: api.expenseCategories,
  permissions: { create: 'expense_categories.create', update: 'expense_categories.update', delete: 'expense_categories.delete' },
  searchPlaceholder: 'Nom, description…',
  defaultSort: 'name',
  hideHeader: true,
  itemLabel: (r) => r.name,
  filters: [{ key: 'is_active', label: 'Statut', options: [{ value: '1', label: 'Actives' }, { value: '0', label: 'Inactives' }] }],
  columns: [
    { key: 'name', label: 'Nom', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'description', label: 'Description' },
    { key: 'expenses_count', label: 'Dépenses', align: 'right' },
    { key: 'is_active', label: 'Statut', badge: 'activeFlag' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 255, wide: true },
    { name: 'description', label: 'Description', type: 'text', nullable: true, max: 255, wide: true },
    { name: 'is_active', label: 'Catégorie active', type: 'toggle', default: true }
  ]
}

// ───────────────────────────── Chauffeurs ─────────────────────────────────
export const driversConfig = {
  key: 'drivers',
  realtime: ['vehicles', 'driver_assignments'], // colonne « Véhicule actuel »
  title: 'Chauffeurs',
  subtitle: 'Les chauffeurs qui encaissent les recettes.',
  singular: 'chauffeur',
  icon: 'driver',
  api: api.drivers,
  permissions: { create: 'drivers.create', update: 'drivers.update', delete: 'drivers.delete' },
  searchPlaceholder: 'Nom, téléphone, permis, pièce d’identité…',
  defaultSort: 'name',
  detailRoute: (r) => `/drivers/${r.id}`,
  itemLabel: (r) => r.name,
  filters: [{ key: 'status', label: 'Statut', options: enumOptions('driverStatus') }],
  columns: [
    { key: 'name', label: 'Nom', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'license_number', label: 'Permis' },
    { key: 'license_expires_at', label: 'Expiration du permis', sortable: 'license_expires_at', date: true },
    { key: 'current_vehicle_id', label: 'Véhicule actuel' },
    { key: 'commission_rate', label: 'Salaire', align: 'right', format: (r) => (r.commission_rate === null || r.commission_rate === undefined ? '—' : `${formatNumber(r.commission_rate)}\u00A0%`) },
    { key: 'status', label: 'Statut', badge: 'driverStatus' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 255 },
    { name: 'phone', label: 'Téléphone', type: 'tel', nullable: true, max: 30 },
    { name: 'email', label: 'E-mail', type: 'email', nullable: true, max: 255 },
    { name: 'id_number', label: 'Numéro de pièce d’identité', type: 'text', nullable: true, max: 50 },
    { name: 'license_number', label: 'Numéro de permis', type: 'text', nullable: true, max: 50 },
    { name: 'license_expires_at', label: 'Expiration du permis', type: 'date', nullable: true },
    { name: 'hired_at', label: 'Date d’embauche', type: 'date', nullable: true },
    { name: 'status', label: 'Statut', type: 'select', options: enumOptions('driverStatus'), default: 'active', required: true },
    // Fixé par un administrateur ; sert de base au calcul de GET /salaries sur la période (écran Salaires).
    // Nom de champ confirmé par une réponse réelle de l'API le 18 octobre 2026.
    { name: 'commission_rate', label: 'Pourcentage de salaire', type: 'number', step: '0.01', min: 0, maxValue: 100, suffix: '%', nullable: true, hint: 'Part des recettes du chauffeur versée en salaire. Laisser vide si non applicable.' },
    { name: 'address', label: 'Adresse', type: 'text', nullable: true, max: 255, wide: true },
    { name: 'user_id', label: 'Compte de connexion (application mobile)', type: 'lookup', lookup: 'users', blank: 'Aucun compte lié', nullable: true, wide: true, hint: 'Pour remplacer un lien, retirez-le d’abord (« Aucun compte lié »), puis enregistrez.', visible: (f, editing) => editing || !f.give_access },
    ...accountFields('du chauffeur (rôle chauffeur, pour l’application mobile)'),
    { name: 'notes', label: 'Notes', type: 'textarea', nullable: true, max: 2000, wide: true }
  ],
  ...accountHooks,
  // Filet de sécurité : si le serveur répond 200 sans reprendre commission_rate (champ
  // confirmé le 18 octobre 2026), on le signale au lieu de laisser croire que c'est enregistré.
  async afterSave(saved, form) {
    const sentRaw = form.commission_rate
    if (sentRaw === '' || sentRaw === null || sentRaw === undefined) return
    const sent = Number(sentRaw)
    const got = saved?.commission_rate === null || saved?.commission_rate === undefined ? null : Number(saved.commission_rate)
    if (got === null || Math.abs(got - sent) > 0.001) {
      throw new Error('Le pourcentage de salaire envoyé n’a pas été repris par le serveur (commission_rate). À vérifier côté back.')
    }
  }
}

// ───────────────────────────── Véhicules ──────────────────────────────────
export const vehiclesConfig = {
  key: 'vehicles',
  realtime: ['drivers', 'driver_assignments', 'vehicle_types', 'owners'], // chauffeur actuel, type, propriétaire
  title: 'Véhicules',
  subtitle: 'La flotte, son statut et son quota journalier effectif.',
  singular: 'véhicule',
  icon: 'vehicle',
  api: api.vehicles,
  permissions: { create: 'vehicles.create', update: 'vehicles.update', delete: 'vehicles.delete' },
  searchPlaceholder: 'Immatriculation, marque, modèle, VIN…',
  defaultSort: 'registration_number',
  detailRoute: (r) => `/vehicles/${r.id}`,
  itemLabel: (r) => r.registration_number,
  filters: [
    { key: 'status', label: 'Statut', options: enumOptions('vehicleStatus') },
    { key: 'owner_id', label: 'Propriétaire', lookup: 'owners', permission: 'owners.view' },
    { key: 'vehicle_type_id', label: 'Type', lookup: 'vehicleTypes', permission: 'vehicle_types.view' }
  ],
  columns: [
    { key: 'registration_number', label: 'Véhicule', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'type', label: 'Type', format: (r) => r.type?.name ?? '—' },
    { key: 'owner', label: 'Propriétaire', format: (r) => r.owner?.name ?? '—' },
    { key: 'current_driver', label: 'Chauffeur', format: (r) => r.current_driver?.name ?? 'Non affecté' },
    { key: 'effective_daily_quota', label: 'Quota / jour', money: true, align: 'right' },
    { key: 'mileage', label: 'Km', sortable: true, align: 'right', format: (r) => formatNumber(r.mileage) },
    { key: 'status', label: 'Statut', badge: 'vehicleStatus' }
  ],
  fields: [
    { name: 'registration_number', label: 'Immatriculation', type: 'text', required: true, max: 30 },
    { name: 'status', label: 'Statut', type: 'select', options: enumOptions('vehicleStatus'), default: 'active', required: true },
    { name: 'owner_id', label: 'Propriétaire', type: 'lookup', lookup: 'owners', required: true },
    { name: 'vehicle_type_id', label: 'Type de véhicule', type: 'lookup', lookup: 'vehicleTypes', required: true },
    { name: 'brand', label: 'Marque', type: 'text', nullable: true, max: 100 },
    { name: 'model', label: 'Modèle', type: 'text', nullable: true, max: 100 },
    { name: 'year', label: 'Année', type: 'integer', min: 1950, nullable: true },
    { name: 'color', label: 'Couleur', type: 'text', nullable: true, max: 50 },
    { name: 'vin', label: 'Numéro de châssis (VIN)', type: 'text', nullable: true, max: 50 },
    { name: 'mileage', label: 'Kilométrage', type: 'integer', min: 0, suffix: 'km' },
    { name: 'daily_quota', label: 'Quota journalier propre', type: 'money', allowZero: true, nullable: true, hint: 'Laisser vide pour utiliser le quota du type de véhicule.' },
    { name: 'acquired_at', label: 'Date d’acquisition', type: 'date', nullable: true },
    { name: 'notes', label: 'Notes', type: 'textarea', nullable: true, max: 2000, wide: true }
  ]
}

// ───────────────────────────── Recettes ───────────────────────────────────
export const revenuesConfig = {
  key: 'revenues',
  title: 'Recettes',
  subtitle: 'Les recettes encaissées par les chauffeurs.',
  singular: 'recette',
  feminine: true,
  icon: 'revenue',
  api: api.revenues,
  permissions: { create: 'revenues.create', update: 'revenues.update', delete: 'revenues.delete' },
  searchPlaceholder: 'Rechercher dans la note…',
  defaultSort: '-date',
  dateRange: true,
  hideHeader: true,
  itemLabel: (r) => `${formatDate(r.date)} · ${vehicleText(r.vehicle)}`,
  filters: [
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'driver_id', label: 'Chauffeur', lookup: 'drivers', permission: 'drivers.view' }
  ],
  columns: [
    { key: 'date', label: 'Date', sortable: true, date: true },
    { key: 'vehicle', label: 'Véhicule', format: (r) => vehicleText(r.vehicle), class: 'font-semibold text-slate-900' },
    { key: 'driver', label: 'Chauffeur', format: (r) => r.driver?.name ?? '—' },
    { key: 'note', label: 'Note' },
    { key: 'amount', label: 'Montant', sortable: true, money: true, align: 'right' }
  ],
  fields: [
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true },
    { name: 'driver_id', label: 'Chauffeur', type: 'lookup', lookup: 'drivers', blank: 'Chauffeur actuel du véhicule', nullable: true, hint: 'Déduit du véhicule s’il n’est pas indiqué.' },
    { name: 'date', label: 'Date', type: 'date', business: true, required: true, default: () => session.today() },
    { name: 'amount', label: 'Montant', type: 'money', required: true },
    { name: 'note', label: 'Note', type: 'text', nullable: true, max: 255, wide: true }
  ]
}

// ───────────────────────────── Dépenses ───────────────────────────────────
const RECEIPT_TYPES = ['image/jpeg', 'image/png', 'application/pdf']
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024

export function validateReceipt(file) {
  if (!RECEIPT_TYPES.includes(file.type)) return 'Format non pris en charge (jpg, png ou pdf).'
  if (file.size > RECEIPT_MAX_BYTES) return 'Le fichier dépasse 5 Mo.'
  return null
}

export const expensesConfig = {
  key: 'expenses',
  title: 'Dépenses',
  subtitle: 'Les dépenses de la flotte, avec justificatif.',
  singular: 'dépense',
  feminine: true,
  icon: 'expense',
  api: api.expenses,
  permissions: { create: 'expenses.create', update: 'expenses.update', delete: 'expenses.delete' },
  searchPlaceholder: 'Rechercher dans la description…',
  defaultSort: '-date',
  dateRange: true,
  hideHeader: true,
  itemLabel: (r) => `${formatDate(r.date)} · ${r.category?.name ?? 'Dépense'}`,
  filters: [
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'expense_category_id', label: 'Catégorie', lookup: 'expenseCategories', permission: 'expense_categories.view' }
  ],
  columns: [
    { key: 'date', label: 'Date', sortable: true, date: true },
    { key: 'vehicle', label: 'Véhicule', format: (r) => vehicleText(r.vehicle), class: 'font-semibold text-slate-900' },
    { key: 'category', label: 'Catégorie', format: (r) => r.category?.name ?? '—' },
    { key: 'description', label: 'Description' },
    { key: 'has_receipt', label: 'Justificatif', bool: true, align: 'center' },
    { key: 'amount', label: 'Montant', sortable: true, money: true, align: 'right' }
  ],
  fields: [
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true },
    { name: 'expense_category_id', label: 'Catégorie', type: 'lookup', lookup: 'expenseCategories', required: true },
    { name: 'date', label: 'Date', type: 'date', business: true, required: true, default: () => session.today() },
    { name: 'amount', label: 'Montant', type: 'money', required: true },
    { name: 'description', label: 'Description', type: 'text', nullable: true, max: 255, wide: true },
    { name: 'receipt', label: 'Justificatif (jpg, png ou pdf — 5 Mo max)', type: 'file', accept: '.jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf', wide: true, validateFile: validateReceipt }
  ],
  // Le justificatif est envoyé en multipart, après l'enregistrement de la dépense.
  async afterSave(saved, form) {
    if (form.receipt) {
      try {
        await api.expenses.uploadReceipt(saved.id, form.receipt)
      } catch (e) {
        e.message = `Dépense enregistrée, mais le justificatif n’a pas pu être joint : ${e.message}`
        throw e
      }
    }
  }
}

// ───────────────────────────── Versements ─────────────────────────────────
export const paymentsConfig = {
  key: 'payments',
  title: 'Versements',
  subtitle: 'Les versements des chauffeurs au propriétaire, à valider par la comptabilité.',
  singular: 'versement',
  icon: 'wallet',
  api: api.payments,
  permissions: { create: 'payments.create', update: 'payments.update', delete: 'payments.delete' },
  searchPlaceholder: 'Référence, note…',
  defaultSort: '-paid_at',
  dateRange: true,
  itemLabel: (r) => `${formatDate(r.paid_at)} · ${vehicleText(r.vehicle)}`,
  // Un versement décidé (validé ou rejeté) ne peut plus être modifié ni supprimé.
  editable: (r) => r.status === 'pending',
  deletable: (r) => r.status === 'pending',
  filters: [
    { key: 'status', label: 'Statut', options: enumOptions('paymentStatus') },
    { key: 'method', label: 'Mode', options: enumOptions('paymentMethod') },
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'driver_id', label: 'Chauffeur', lookup: 'drivers', permission: 'drivers.view' }
  ],
  columns: [
    { key: 'paid_at', label: 'Date', sortable: true, date: true },
    { key: 'vehicle', label: 'Véhicule', format: (r) => vehicleText(r.vehicle), class: 'font-semibold text-slate-900' },
    { key: 'driver', label: 'Chauffeur', format: (r) => r.driver?.name ?? '—' },
    { key: 'method', label: 'Mode', badge: 'paymentMethod' },
    { key: 'reference', label: 'Référence' },
    { key: 'amount', label: 'Montant', sortable: true, money: true, align: 'right' },
    { key: 'status', label: 'Statut', badge: 'paymentStatus' }
  ],
  fields: [
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true },
    { name: 'driver_id', label: 'Chauffeur', type: 'lookup', lookup: 'drivers', blank: 'Chauffeur actuel du véhicule', nullable: true },
    { name: 'paid_at', label: 'Date du versement', type: 'date', business: true, required: true, default: () => session.today() },
    { name: 'amount', label: 'Montant', type: 'money', required: true },
    { name: 'method', label: 'Mode de paiement', type: 'select', options: enumOptions('paymentMethod'), default: 'cash', required: true },
    { name: 'reference', label: 'Référence', type: 'text', nullable: true, max: 100 },
    { name: 'note', label: 'Note', type: 'text', nullable: true, max: 255, wide: true }
  ]
}

// ───────────────────────────── Carburant ──────────────────────────────────
export const fuelLogsConfig = {
  key: 'fuel-logs',
  title: 'Carburant',
  subtitle: 'Les pleins de carburant de la flotte.',
  singular: 'plein',
  icon: 'fuel',
  api: api.fuelLogs,
  permissions: { create: 'fuel_logs.create', update: 'fuel_logs.update', delete: 'fuel_logs.delete' },
  searchPlaceholder: 'Nom de la station…',
  defaultSort: '-date',
  dateRange: true,
  hideHeader: true,
  createLabel: 'Nouveau plein',
  itemLabel: (r) => `${formatDate(r.date)} · ${vehicleText(r.vehicle)}`,
  filters: [
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'driver_id', label: 'Chauffeur', lookup: 'drivers', permission: 'drivers.view' },
    { key: 'full_tank', label: 'Plein complet', options: yesNo }
  ],
  columns: [
    { key: 'date', label: 'Date', sortable: true, date: true },
    { key: 'vehicle', label: 'Véhicule', format: (r) => vehicleText(r.vehicle), class: 'font-semibold text-slate-900' },
    { key: 'driver', label: 'Chauffeur', format: (r) => r.driver?.name ?? '—' },
    { key: 'liters', label: 'Litres', sortable: true, align: 'right', format: (r) => formatNumber(r.liters) },
    { key: 'price_per_liter', label: 'Prix / litre', money: true, align: 'right' },
    { key: 'amount', label: 'Montant', sortable: true, money: true, align: 'right' },
    { key: 'odometer', label: 'Km', sortable: true, align: 'right', format: (r) => (r.odometer === null ? '—' : formatNumber(r.odometer)) },
    { key: 'full_tank', label: 'Complet', bool: true, align: 'center' },
    { key: 'station', label: 'Station' }
  ],
  fields: [
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true },
    { name: 'driver_id', label: 'Chauffeur', type: 'lookup', lookup: 'drivers', blank: 'Non renseigné', nullable: true },
    { name: 'date', label: 'Date', type: 'date', business: true, required: true, default: () => session.today() },
    { name: 'liters', label: 'Volume', type: 'number', step: 'any', min: 0, required: true, suffix: 'L' },
    {
      name: 'mode',
      label: 'Saisir…',
      type: 'segmented',
      virtual: true,
      wide: true,
      default: 'price',
      options: [
        { value: 'price', label: 'Le prix du litre' },
        { value: 'total', label: 'Le total du ticket' }
      ]
    },
    { name: 'price_per_liter', label: 'Prix du litre', type: 'money', required: true, visible: (f) => f.mode !== 'total' },
    { name: 'amount', label: 'Total du ticket', type: 'money', required: true, visible: (f) => f.mode === 'total', hint: 'Le prix du litre est recalculé par le serveur.' },
    { name: 'odometer', label: 'Kilométrage relevé', type: 'integer', min: 0, nullable: true, suffix: 'km' },
    { name: 'station', label: 'Station', type: 'text', nullable: true, max: 255 },
    { name: 'full_tank', label: 'Plein complet', type: 'toggle', default: true }
  ]
}

// ───────────────────────── Maintenance : plans ────────────────────────────
export const maintenancePlansConfig = {
  key: 'maintenance-plans',
  title: 'Plans de maintenance',
  subtitle: 'Les entretiens récurrents, par véhicule ou par type de véhicule.',
  singular: 'plan',
  icon: 'wrench',
  api: api.maintenancePlans,
  permissions: { create: 'maintenance_plans.create', update: 'maintenance_plans.update', delete: 'maintenance_plans.delete' },
  searchPlaceholder: 'Nom, description…',
  defaultSort: 'name',
  hideHeader: true,
  createLabel: 'Nouveau plan',
  itemLabel: (r) => r.name,
  filters: [
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'vehicle_type_id', label: 'Type', lookup: 'vehicleTypes', permission: 'vehicle_types.view' },
    { key: 'is_active', label: 'Statut', options: [{ value: '1', label: 'Actifs' }, { value: '0', label: 'Inactifs' }] }
  ],
  columns: [
    { key: 'name', label: 'Plan', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'target', label: 'Concerne', format: (r) => (r.vehicle ? r.vehicle.registration_number : r.vehicle_type ? `Type : ${r.vehicle_type.name}` : '—') },
    {
      key: 'interval',
      label: 'Intervalle',
      format: (r) => [r.interval_km ? `${formatNumber(r.interval_km)} km` : null, r.interval_days ? `${r.interval_days} j` : null].filter(Boolean).join(' / ') || '—'
    },
    { key: 'alert', label: 'Alerte à', format: (r) => `${formatNumber(r.alert_km)} km / ${r.alert_days} j` },
    { key: 'is_active', label: 'Statut', badge: 'activeFlag' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 255, wide: true },
    {
      name: 'target',
      label: 'Le plan concerne…',
      type: 'segmented',
      virtual: true,
      wide: true,
      default: 'vehicle',
      options: [
        { value: 'vehicle', label: 'Un véhicule' },
        { value: 'type', label: 'Tous les véhicules d’un type' }
      ]
    },
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true, nullable: true, visible: (f) => f.target !== 'type' },
    { name: 'vehicle_type_id', label: 'Type de véhicule', type: 'lookup', lookup: 'vehicleTypes', required: true, nullable: true, visible: (f) => f.target === 'type' },
    { name: 'interval_km', label: 'Tous les…', type: 'integer', min: 1, nullable: true, suffix: 'km', hint: 'Un intervalle en km et/ou en jours.' },
    { name: 'interval_days', label: 'Ou tous les…', type: 'integer', min: 1, nullable: true, suffix: 'jours' },
    { name: 'alert_km', label: 'Alerter avant', type: 'integer', min: 0, suffix: 'km' },
    { name: 'alert_days', label: 'Alerter avant', type: 'integer', min: 0, suffix: 'jours' },
    { name: 'description', label: 'Description', type: 'text', nullable: true, max: 255, wide: true },
    { name: 'is_active', label: 'Plan actif', type: 'toggle', default: true }
  ],
  toForm: (r) => ({ target: r.vehicle_type_id && !r.vehicle_id ? 'type' : 'vehicle' }),
  validate: (f) => (!f.interval_km && !f.interval_days ? { interval_km: ['Indiquez un intervalle en km et/ou en jours.'] } : {}),
  toPayload: (form, { defaults }) => ({
    ...defaults,
    vehicle_id: form.target === 'type' ? null : defaults.vehicle_id,
    vehicle_type_id: form.target === 'type' ? defaults.vehicle_type_id : null
  })
}

// ─────────────────────── Maintenance : interventions ──────────────────────
export const maintenanceRecordsConfig = {
  key: 'maintenance-records',
  title: 'Interventions',
  subtitle: 'L’historique des entretiens et réparations.',
  singular: 'intervention',
  feminine: true,
  icon: 'wrench',
  api: api.maintenanceRecords,
  permissions: { create: 'maintenance_records.create', update: 'maintenance_records.update', delete: 'maintenance_records.delete' },
  searchPlaceholder: 'Titre, description, prestataire…',
  defaultSort: '-performed_at',
  dateRange: true,
  hideHeader: true,
  createLabel: 'Nouvelle intervention',
  itemLabel: (r) => `${formatDate(r.performed_at)} · ${r.title}`,
  filters: [
    { key: 'vehicle_id', label: 'Véhicule', lookup: 'vehicles', permission: 'vehicles.view' },
    { key: 'maintenance_plan_id', label: 'Plan', lookup: 'maintenancePlans', permission: 'maintenance_plans.view' }
  ],
  columns: [
    { key: 'performed_at', label: 'Date', sortable: true, date: true },
    { key: 'vehicle', label: 'Véhicule', format: (r) => vehicleText(r.vehicle), class: 'font-semibold text-slate-900' },
    { key: 'title', label: 'Intervention' },
    { key: 'plan', label: 'Plan', format: (r) => r.plan?.name ?? '—' },
    { key: 'provider', label: 'Prestataire' },
    { key: 'odometer', label: 'Km', sortable: true, align: 'right', format: (r) => (r.odometer === null ? '—' : formatNumber(r.odometer)) },
    { key: 'cost', label: 'Coût', sortable: true, money: true, align: 'right' }
  ],
  fields: [
    { name: 'vehicle_id', label: 'Véhicule', type: 'lookup', lookup: 'vehicles', required: true },
    { name: 'maintenance_plan_id', label: 'Plan de maintenance', type: 'lookup', lookup: 'maintenancePlans', blank: 'Aucun plan', nullable: true, hint: 'Rattachée à un plan, elle repousse son échéance.' },
    { name: 'title', label: 'Intitulé', type: 'text', required: true, max: 255, wide: true },
    { name: 'performed_at', label: 'Date de l’intervention', type: 'date', business: true, required: true, default: () => session.today() },
    { name: 'cost', label: 'Coût', type: 'money', allowZero: true },
    { name: 'odometer', label: 'Kilométrage', type: 'integer', min: 0, nullable: true, suffix: 'km' },
    { name: 'provider', label: 'Prestataire', type: 'text', nullable: true, max: 255 },
    { name: 'next_due_km', label: 'Prochaine échéance (km)', type: 'integer', min: 0, nullable: true, suffix: 'km' },
    { name: 'next_due_at', label: 'Prochaine échéance (date)', type: 'date', nullable: true },
    { name: 'description', label: 'Description', type: 'textarea', nullable: true, max: 2000, wide: true }
  ]
}

// ───────────────────────────── Utilisateurs ───────────────────────────────
export const usersConfig = {
  key: 'users',
  title: 'Utilisateurs',
  subtitle: 'Les comptes de connexion et leur rôle.',
  singular: 'utilisateur',
  icon: 'users',
  api: api.users,
  permissions: { create: 'users.create', update: 'users.update', delete: 'users.delete' },
  searchPlaceholder: 'Nom, e-mail, téléphone…',
  defaultSort: 'name',
  itemLabel: (r) => r.name,
  deletable: (r) => r.id !== session.user?.id,
  filters: [{ key: 'is_active', label: 'Compte', options: [{ value: '1', label: 'Actifs' }, { value: '0', label: 'Désactivés' }] }],
  columns: [
    { key: 'name', label: 'Nom', sortable: true, class: 'font-semibold text-slate-900' },
    { key: 'email', label: 'E-mail' },
    { key: 'phone', label: 'Téléphone' },
    { key: 'role', label: 'Rôle', badge: 'role' },
    { key: 'last_login_at', label: 'Dernière connexion', datetime: true },
    { key: 'is_active', label: 'Compte', badge: 'activeFlag' }
  ],
  fields: [
    { name: 'name', label: 'Nom', type: 'text', required: true, max: 255 },
    { name: 'role', label: 'Rôle', type: 'lookup', lookup: 'roles', required: true, stringValue: true },
    { name: 'email', label: 'E-mail', type: 'email', nullable: true, max: 255, autocomplete: 'off', hint: 'Unique sur toute la plateforme.' },
    { name: 'phone', label: 'Téléphone', type: 'tel', nullable: true, max: 30, placeholder: '+242064265595', hint: 'Format international conseillé ; sans « + » ni « 00 », le numéro est considéré comme local (+242). Le zéro initial fait partie du numéro.' },
    { name: 'password', label: 'Mot de passe', type: 'password', required: true, requiredOnEdit: false, autocomplete: 'new-password', generate: true, hint: 'Au moins 8 caractères. À la modification, laissez vide pour conserver le mot de passe actuel.', notice: 'Si un e-mail est renseigné, la personne sera prévenue de la création de son compte. Le mot de passe ne lui est pas envoyé : communiquez-le-lui vous-même.', wide: true },
    { name: 'is_active', label: 'Compte actif', type: 'toggle', default: true, hint: 'Désactiver un compte révoque ses jetons.' }
  ],
  validate: (f) => (!f.email && !f.phone ? { email: ['Indiquez un e-mail ou un téléphone.'] } : {})
}
